import React, { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Cpu, Server, Zap, Clock, Users, CheckCircle, Settings, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ANIMATION_VARIANTS, HERO_CONTENT } from '../constants';
import { 
  useFloatingParticles,
  usePerformanceMonitoring,
  useOptimizedBackground
} from '../hooks/useAnimations';
import { GlassCard, BenefitCard, StyledIcon, FeatureList } from '../components/animations';
import { handlePageNavigation } from '../utils/navigation';

// Scroll-Based Timeline Component for GPU Leasing Process
const ScrollBasedTimeline: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Detect screen size
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 80%", "end 20%"] // Even earlier start for smoother animations
  });

  // Smooth spring animation for performance
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Prevent backtracking - once progress reaches a point, it stays there
  const [maxProgress, setMaxProgress] = useState(0);
  
  useEffect(() => {
    const unsubscribe = smoothProgress.onChange((latest) => {
      if (latest > maxProgress) {
        setMaxProgress(latest);
      }
    });
    return unsubscribe;
  }, [smoothProgress, maxProgress]);

  // Use maxProgress instead of smoothProgress to prevent backtracking
  const pathLength = useTransform(() => Math.min(maxProgress * 3, 1)); // Faster wave drawing
  
  // Individual card visibility - slower, more spaced timing
  const card1Progress = useTransform(() => Math.min(Math.max((maxProgress - 0.05) * 6, 0), 1));
  const card2Progress = useTransform(() => Math.min(Math.max((maxProgress - 0.12) * 6, 0), 1));
  const card3Progress = useTransform(() => Math.min(Math.max((maxProgress - 0.19) * 6, 0), 1));
  const card4Progress = useTransform(() => Math.min(Math.max((maxProgress - 0.26) * 6, 0), 1));

  // Calculate responsive positions based on sine wave
  const calculatePosition = (index: number, total: number) => {
    if (isMobile) {
      // Position cards with sine wave in between on mobile
      return {
        x: index % 2 === 0 ? "12%" : "52%",  // Alternate left and right
        y: `${15 + (index * 18)}%`  // Closer vertical spacing
      };
    }
    
    // Desktop: distribute along sine wave
    const segmentWidth = 70 / total; // Use 70% of width for better margins
    const xPercent = 15 + (index * segmentWidth); // Start at 15% for margin
    
    // Sine wave calculation: peaks at odd indices, valleys at even
    const yPercent = index % 2 === 0 ? 15 : 55; // Alternate between peak and valley
    
    return {
      x: `${xPercent}%`,
      y: `${yPercent}%`
    };
  };

  const processSteps = [
    {
      step: "1",
      title: "Consultation",
      description: "Discuss your project needs with our experts to determine the ideal GPU node configuration, leasing plan, and deployment model.",
      color: "blue",
      gradient: "from-blue-500 to-cyan-600",
      progress: card1Progress
    },
    {
      step: "2", 
      title: "Node Provisioning",
      description: "We provision the NVIDIA GPU nodes based on your specific requirements and ensure they are ready for immediate use.",
      color: "purple",
      gradient: "from-purple-500 to-pink-600",
      progress: card2Progress
    },
    {
      step: "3",
      title: "Integration & Setup", 
      description: "Our team will assist with integrating the leased nodes into your workflow, whether in the cloud or on-premise.",
      color: "emerald",
      gradient: "from-emerald-500 to-teal-600",
      progress: card3Progress
    },
    {
      step: "4",
      title: "Ongoing Support",
      description: "We manage the hardware, provide system updates, and offer 24/7 support to keep your leased nodes running optimally.",
      color: "orange",
      gradient: "from-orange-500 to-red-600",
      progress: card4Progress
    }
  ].map((step, index) => ({
    ...step,
    position: calculatePosition(index, 4)
  }));

  return (
    <motion.div 
      ref={containerRef}
      className="mb-20 relative"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={ANIMATION_VARIANTS.staggerChildren}
    >
      <motion.h2 
        className="heading-2 text-white-gradient mb-16 text-center"
        variants={ANIMATION_VARIANTS.slideUp}
      >
        How It <span className="text-primary-gradient">Works</span>
      </motion.h2>
      
      {/* Scroll-controlled Timeline Container - Responsive */}
      <div className={`relative overflow-visible ${isMobile ? 'h-[800px]' : 'h-[600px]'} -mx-4 px-4 md:-mx-8 md:px-8 lg:-mx-16 lg:px-16`}>
        
        {
        <motion.svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox={isMobile ? "0 0 400 1000" : "0 0 1000 400"}
          preserveAspectRatio="xMidYMid meet"
          style={{ zIndex: 1, opacity: isMobile ? 0.4 : 1 }}
        >
          <defs>
            <linearGradient id="scrollTimelineGradient" x1={isMobile ? "0%" : "0%"} y1={isMobile ? "0%" : "0%"} x2={isMobile ? "0%" : "100%"} y2={isMobile ? "100%" : "0%"}>
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.001" />
              <stop offset="10%" stopColor="#3b82f6" stopOpacity="0.05" />
              <stop offset="20%" stopColor="#3b82f6" stopOpacity="0.6" />
              <stop offset="33%" stopColor="#8b5cf6" stopOpacity="0.9" />
              <stop offset="66%" stopColor="#06b6d4" stopOpacity="0.8" />
              <stop offset="80%" stopColor="#10b981" stopOpacity="0.6" />
              <stop offset="90%" stopColor="#10b981" stopOpacity="0.05" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0.001" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
              <feMerge> 
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          
          {/* Sine wave - horizontal for desktop, vertical for mobile */}
          <motion.path
            d={isMobile ? "M200 -100Q270 0 200 80q-70 100 0 200t0 200 0 200 0 200q-70 120 0 220" : "M-100 200Q0 270 80 200q100-70 200 0t200 0 200 0 200 0q120-70 220 0"}            
            stroke="url(#scrollTimelineGradient)"
            strokeWidth="6"
            fill="none"
            strokeLinecap="round"
            filter="url(#glow)"
            style={{ pathLength }}
            initial={{ pathLength: 0 }}
          />
          
          {/* Wave position markers for precise card placement */}
          <motion.circle cx="160" cy="200" r="4" fill="#3b82f6" opacity="0.6" />
          <motion.circle cx="340" cy="200" r="4" fill="#8b5cf6" opacity="0.6" />
          <motion.circle cx="520" cy="200" r="4" fill="#06b6d4" opacity="0.6" />
          <motion.circle cx="700" cy="200" r="4" fill="#10b981" opacity="0.6" />
          
          {/* Animated particle traveling along path */}
          <motion.circle
            cx="-100"
            cy="200"
            r="8"
            fill="#3b82f6"
            style={{
              offsetDistance: smoothProgress,
              offsetPath: "path('M-100,200 Q-20,250 40,200 Q100,150 160,200 Q220,250 280,200 Q340,150 400,200 Q460,250 520,200 Q580,150 640,200 Q700,250 760,200 Q820,150 880,200 Q940,250 1000,200 Q1060,150 1120,200')"
            }}
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.6, 1, 0.6]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </motion.svg>
        }

        {/* Process Step Cards - Precisely positioned at wave peaks/valleys */}
        {processSteps.map((step) => (
          <motion.div
            key={step.step}
            className={`absolute ${isMobile ? 'w-[40%] max-w-[160px]' : 'w-[280px]'}`}
            style={{
              left: step.position.x,
              top: step.position.y,
              transform: "translateX(-50%) translateY(-50%)",
              zIndex: 10,
              opacity: useTransform(step.progress, [0, 0.2, 1], [0, 0.8, 1]),
              scale: useTransform(step.progress, [0, 0.3, 1], [0.8, 0.95, 1]),
              y: useTransform(step.progress, [0, 1], [20, 0]),
            }}
            transition={{
              duration: 0.8,
              ease: [0.25, 0.46, 0.45, 0.94], // Custom bezier for smooth entrance
            }}
          >
            {/* Compact Futuristic Card */}
            <motion.div
              className="relative group"
              whileHover={{ rotateY: 5 }}
              style={{ perspective: "1000px" }}
            >
              <div className={`${isMobile ? 'p-3' : 'w-48 p-4'} rounded-2xl relative overflow-hidden backdrop-blur-xl bg-black/40 border border-white/20 shadow-2xl`}>
                {/* Holographic background effect */}
                <motion.div
                  className={`absolute inset-0 bg-gradient-to-br ${step.gradient} opacity-0 group-hover:opacity-20 transition-opacity duration-500 rounded-2xl`}
                />
                
                {/* Animated border glow */}
                <motion.div
                  className={`absolute inset-0 rounded-2xl border-2 border-transparent bg-gradient-to-br ${step.gradient} opacity-0 group-hover:opacity-30`}
                  style={{ mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)" }}
                  animate={{
                    opacity: [0, 0.3, 0],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
                
                {/* Step number */}
                <motion.div 
                  className="flex items-center justify-center mb-3"
                >
                  <motion.div 
                    className={`w-12 h-12 bg-gradient-to-br from-${step.color}-500/20 to-${step.color}-600/30 rounded-xl border border-${step.color}-400/30 flex items-center justify-center relative overflow-hidden`}
                    whileHover={{ rotate: 15 }}
                    transition={{ duration: 0.4 }}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br from-${step.color}-400/20 to-${step.color}-600/30 rounded-xl blur-md transition-all duration-500`} />
                    <span className={`text-lg font-bold text-${step.color}-400 relative z-40`}>{step.step}</span>
                  </motion.div>
                </motion.div>
                
                <motion.h3 
                  className={`${isMobile ? 'text-sm' : 'text-base'} font-bold text-white mb-2 text-center group-hover:text-gray-100 transition-colors duration-300`}
                  whileHover={{ y: -1 }}
                >
                  {step.title}
                </motion.h3>
                
                {!isMobile && (
                  <motion.p 
                    className="text-gray-300 text-xs leading-relaxed text-center group-hover:text-gray-200 transition-colors duration-300"
                    initial={{ opacity: 0.8 }}
                    whileHover={{ opacity: 1 }}
                  >
                    {step.description}
                  </motion.p>
                )}

                {/* Digital corner indicators */}
                <div className="absolute top-1 right-1 w-1 h-1 bg-white/40 rounded-full" />
                <div className="absolute bottom-1 left-1 w-1 h-1 bg-white/40 rounded-full" />
                
                {/* Scan line effect */}
                <motion.div
                  className="absolute inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-white/50 to-transparent"
                  animate={{
                    y: [-20, 80, -20]
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                />
              </div>
            </motion.div>
          </motion.div>
        ))}
        
        {/* Floating digital particles */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400/60 rounded-full"
            style={{
              left: `${20 + i * 15}%`,
              top: `${30 + Math.sin(i) * 20}%`
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.3, 0.8, 0.3],
              scale: [1, 1.5, 1]
            }}
            transition={{
              duration: 3 + i * 0.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.7
            }}
          />
        ))}
      </div>
    </motion.div>
  );
};

const GPULeasingPage: React.FC = () => {
  usePerformanceMonitoring('GPULeasingPage');
  
  // Global floating particles for the entire page (mandatory pattern)
  const { particlesRef, containerClasses: particlesContainerClasses, particles } = useFloatingParticles(50);

  // Optimized backgrounds for alternating sections
  const { backgroundClasses: heroBackgroundClasses, backgroundStyle: heroBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: overviewBackgroundClasses, backgroundStyle: overviewBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: featuresBackgroundClasses, backgroundStyle: featuresBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: processBackgroundClasses, backgroundStyle: processBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: benefitsBackgroundClasses, backgroundStyle: benefitsBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: ctaBackgroundClasses, backgroundStyle: ctaBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={ANIMATION_VARIANTS.fadeIn}
      className="min-h-screen relative"
    >
      {/* Global Floating Particles Background */}
      <div ref={particlesRef} className={`${particlesContainerClasses} fixed inset-0 z-30`}>
        {particles.map((particle) => (
          <div
            key={particle.id}
            className={particle.classes}
            style={particle.style}
          />
        ))}
      </div>

      <div className="relative z-20">
        {/* Hero Section */}
        <section className="section-padding relative text-white">
          {/* Hero Background - Normal orientation */}
          <div 
            className={heroBackgroundClasses}
            style={heroBackgroundStyle}
          />
          
          <div className="container relative z-40 pt-32 mb-14">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
              className="max-w-4xl mx-auto text-center"
            >
              <motion.div 
                className="flex items-center justify-center mb-6"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                <StyledIcon 
                  icon={Cpu} 
                  size="xl" 
                  color="primary" 
                  variant="static" 
                />
              </motion.div>

              <motion.h1
                variants={ANIMATION_VARIANTS.slideUp}
                className="heading-1 mb-6"
              >
                NVIDIA <span className="text-primary-gradient">GPU Node Leasing</span>
              </motion.h1>
              
              <motion.p
                variants={ANIMATION_VARIANTS.slideUp}
                className="hero-subtitle text-center mb-12 max-w-3xl mx-auto"
              >
                Supercharge your computing capabilities with our NVIDIA GPU Node Leasing Service. 
                Designed for businesses, researchers, and developers who require high-performance GPU resources, 
                this service provides flexible access to cutting-edge NVIDIA GPU nodes without the overhead of purchasing and maintaining hardware.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Service Overview Section */}
        <section className="section-padding relative text-white">
          {/* Overview Background - Rotated orientation for alternating effect */}
          <div 
            className={overviewBackgroundClasses}
            style={{
              ...overviewBackgroundStyle,
              transform: 'translateZ(0) rotate(180deg)',
              backgroundPosition: 'bottom',
            }}
          />
          
          <div className="container relative z-40">
            <GlassCard className="p-8 lg:p-12 rounded-3xl">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={ANIMATION_VARIANTS.staggerChildren}
                >
                  <motion.h2 
                    className="text-3xl lg:text-4xl font-bold mb-6"
                    variants={ANIMATION_VARIANTS.slideUp}
                  >
                    Why Choose Our <span className="text-primary-gradient">NVIDIA GPU Node Leasing Service?</span>
                  </motion.h2>
                  <motion.p 
                    className="text-lg text-gray-300 mb-8 leading-relaxed"
                    variants={ANIMATION_VARIANTS.slideUp}
                  >
                    Ideal for AI, machine learning, data science, and other compute-intensive tasks, 
                    our leasing solution offers scalability, affordability, and on-demand power.
                  </motion.p>
                  
                  <motion.div 
                    className="mb-8"
                    variants={ANIMATION_VARIANTS.staggerChildren}
                  >
                    <FeatureList 
                      features={[
                        "Access the Latest NVIDIA GPUs: H100, A100, L40, or RTX series GPUs",
                        "No Upfront Costs: Avoid capital expense of buying GPUs outright",
                        "Flexible Leasing Plans: Tailored to your project's duration and scaling needs",
                        "Fully Managed Nodes: We handle infrastructure while you focus on your work"
                      ]}
                      animated={false}
                    />
                  </motion.div>
                </motion.div>
                
                <motion.div 
                  className="relative"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={ANIMATION_VARIANTS.slideUp}
                >
                  <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                    <img 
                      src="/assets/images/services/nvidia-gpu.jpg"
                      alt="NVIDIA GPU Node Leasing Infrastructure"
                      className="w-full h-80 object-cover"
                    />
                    {/* Overlay gradient for better text contrast */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-black/20 via-transparent to-primary-500/10"></div>
                    
                    {/* Optional floating badge */}
                    <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2 border border-primary-400/30">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span className="text-white text-xs font-medium">NVIDIA GPU Nodes</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </GlassCard>
          </div>
        </section>

        {/* Key Features Section */}
        <section className="section-padding relative text-white">
          {/* Features Background - Normal orientation */}
          <div 
            className={featuresBackgroundClasses}
            style={featuresBackgroundStyle}
          />
          
          <div className="container relative z-40">
            <motion.div 
              className="mb-20"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
            >
              <motion.h2 
                className="heading-2 mb-16 text-center"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                Key <span className="text-primary-gradient">Features</span>
              </motion.h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                <BenefitCard
                  icon={Cpu}
                  title="State-of-the-Art NVIDIA GPUs"
                  description="Access the latest in GPU technology, including the powerful NVIDIA H100, A100, L40, or RTX series GPUs, providing unparalleled performance for AI and HPC tasks."
                  color="blue"
                  delay={0.1}
                />
                <BenefitCard
                  icon={Settings}
                  title="Customizable Configurations"
                  description="Choose the right GPU node configuration for your workload, whether you need high-memory GPUs, multi-GPU setups, or enhanced networking options."
                  color="emerald"
                  delay={0.2}
                />
                <BenefitCard
                  icon={Server}
                  title="Seamless Cloud Integration"
                  description="Deploy GPU nodes in the cloud, ensuring seamless operation no matter where your data resides."
                  color="cyan"
                  delay={0.3}
                />
                <BenefitCard
                  icon={Zap}
                  title="Optimized for AI & ML Workloads"
                  description="Our GPU nodes are specifically optimized for high-performance computing applications, including AI model training, deep learning frameworks, and big data analysis."
                  color="purple"
                  delay={0.4}
                />
                <BenefitCard
                  icon={Clock}
                  title="24/7 Monitoring & Support"
                  description="Our team of experts provides continuous monitoring and support, ensuring high availability and reliability for your leased GPU nodes."
                  color="orange"
                  delay={0.5}
                />
                <BenefitCard
                  icon={TrendingUp}
                  title="Scale as You Go"
                  description="Start with the resources you need and scale up or down as your project evolves. Whether you're a startup or large enterprise, our service adapts to your needs."
                  color="green"
                  delay={0.6}
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Process Section */}
        <section className="section-padding relative text-white">
          {/* Process Background - Rotated orientation */}
          <div 
            className={processBackgroundClasses}
            style={{
              ...processBackgroundStyle,
              transform: 'translateZ(0) rotate(180deg)',
              backgroundPosition: 'bottom',
            }}
          />
          
          <div className="container relative z-40">
            <ScrollBasedTimeline />
          </div>
        </section>

        {/* Benefits Section */}
        <section className="section-padding relative text-white">
          {/* Benefits Background - Normal orientation */}
          <div 
            className={benefitsBackgroundClasses}
            style={benefitsBackgroundStyle}
          />
          
          <div className="container relative z-40">
            <motion.div 
              className="mb-20"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
            >
              <motion.h2 
                className="heading-2 mb-16 text-center"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                Benefits of Leasing <span className="text-primary-gradient">NVIDIA GPU Nodes</span>
              </motion.h2>
              
              <div className="grid lg:grid-cols-2 gap-8">
                <BenefitCard
                  icon={CheckCircle}
                  title="Cost-Efficient"
                  description="Pay only for the GPU resources you need, avoiding the high costs of purchasing and maintaining hardware. Get enterprise-grade performance without the capital investment."
                  color="emerald"
                  delay={0.1}
                />
                <BenefitCard
                  icon={Zap}
                  title="On-Demand Power"
                  description="Access powerful GPUs when you need them, enabling faster project completion and reduced time-to-market for your AI and ML initiatives."
                  color="blue"
                  delay={0.2}
                />
                <BenefitCard
                  icon={TrendingUp}
                  title="Risk-Free Scaling"
                  description="Easily scale your GPU resources up or down, allowing you to adapt to project changes without long-term commitments or risks."
                  color="purple"
                  delay={0.3}
                />
                <BenefitCard
                  icon={Users}
                  title="Expert Management"
                  description="Our team handles all hardware management, system updates, and maintenance, allowing you to focus entirely on your core business and research objectives."
                  color="orange"
                  delay={0.4}
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding relative text-white">
          {/* CTA Background - Rotated orientation */}
          <div 
            className={ctaBackgroundClasses}
            style={{
              ...ctaBackgroundStyle,
              transform: 'translateZ(0) rotate(180deg)',
              backgroundPosition: 'bottom',
            }}
          />
          
          <div className="container relative z-40">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.h2 
                className="heading-2 mb-6"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                Get Started <span className="text-primary-gradient">Today</span>
              </motion.h2>
              <motion.p 
                className="body-large mb-8"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                Accelerate your AI and compute-intensive projects with the power of NVIDIA GPUs. 
                Contact us today to explore our NVIDIA GPU Node Leasing Service and discover how we can help fuel your business's growth.
              </motion.p>
              <motion.div 
                className="text-center"
                variants={ANIMATION_VARIANTS.slideUp}
              >
                <div className="flex flex-col sm:flex-row gap-4 mt-auto justify-center items-center">
                    <Link
                        to="/contact"
                        className="btn-futuristic"
                        onClick={handlePageNavigation}
                    >
                        Start Leasing
                    </Link>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </div>
    </motion.div>
  );
};

export default GPULeasingPage;
